import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

// Material UI
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

// Icons
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import SearchIcon from "@mui/icons-material/Search";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";

// YUVA Project Components
import BackButton from "../../components/common/BackButton";
import { CardGrid } from "../../components/common/CardGrid";
import Loader from "../../components/common/Loader";

// CSS & Utilities
import css from "../../css/admin/documentation.module.css";
import { SERVER_ORIGIN } from "../../utilities/constants";
import {
  hasAdminAccess,
  hasReadOnlyAccess,
  getDashboardPath,
} from "../../utilities/helper_functions";

const DocumentationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("cards"); // 'cards' | 'list'

  // Modals
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Upload Form State
  const [uploadForm, setUploadForm] = useState({
    title: "",
    category: "User Manual",
    customCategory: "",
    pages: "",
    description: "",
    file: null,
  });

  const isAdmin =
    hasAdminAccess() || sessionStorage.getItem("userType") === "admin";
  const isReadOnly = hasReadOnlyAccess();

  const getBaseApiUrl = () => {
    if (
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1")
    ) {
      return "http://localhost:3001";
    }
    return SERVER_ORIGIN;
  };

  // If redirected with ?upload=true, open upload dialog
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("upload") === "true") {
      setIsUploadOpen(true);
    }
  }, [location.search]);

  // Fetch documents from backend API
  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const base = getBaseApiUrl();
      const endpoints = [
        `${base}/api/documentation`,
        "/api/documentation",
        `${SERVER_ORIGIN}/api/documentation`,
      ];

      let fetchedData = null;
      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
            },
          });
          if (res.ok) {
            const data = await res.json();
            if (data?.documents && data.documents.length > 0) {
              fetchedData = data.documents;
              break;
            }
          }
        } catch (e) {
          // continue trying next endpoint
        }
      }

      if (fetchedData && fetchedData.length > 0) {
        const formatted = fetchedData.map((d) => {
          let dateStr = "Jan 6, 2026";
          if (d.createdAt) {
            const dt = new Date(d.createdAt);
            dateStr = dt.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            });
          }
          return {
            ...d,
            date: dateStr,
            size:
              d.fileSize ||
              `${(d.fileSizeBytes ? d.fileSizeBytes / (1024 * 1024) : 2.5).toFixed(
                1
              )} MB`,
          };
        });
        setDocuments(formatted);
      } else {
        setDocuments([]);
      }
    } catch (err) {
      console.warn("Could not load documents from server:", err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Category counts computed from documents
  const categoriesList = useMemo(() => {
    const map = { All: documents.length };
    documents.forEach((doc) => {
      const cat = (doc.category || "User Manual").trim();
      map[cat] = (map[cat] || 0) + 1;
    });
    return map;
  }, [documents]);

  // Filtered documents by search & category
  const filteredDocuments = useMemo(() => {
    let result = [...documents];

    // Category filter
    if (selectedCategory !== "All") {
      result = result.filter(
        (d) =>
          (d.category || "").toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Search filter
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      result = result.filter(
        (d) =>
          d.title?.toLowerCase().includes(query) ||
          d.category?.toLowerCase().includes(query) ||
          d.description?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [documents, selectedCategory, searchTerm]);

  // Submit Upload Form
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadForm.title.trim()) {
      toast.error("Please enter a document title");
      return;
    }
    if (!uploadForm.file) {
      toast.error("Please select a PDF file to upload");
      return;
    }

    const categoryName =
      uploadForm.category === "Custom"
        ? uploadForm.customCategory.trim() || "User Manual"
        : uploadForm.category;

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("title", uploadForm.title.trim());
      formData.append("category", categoryName);
      formData.append("pages", uploadForm.pages || 1);
      formData.append("description", uploadForm.description || "");
      formData.append("document_file", uploadForm.file);
      formData.append(
        "uploadedBy",
        sessionStorage.getItem("fName") || "Admin"
      );

      const base = getBaseApiUrl();
      const endpoints = [
        `${base}/api/documentation/upload`,
        "/api/documentation/upload",
        `${SERVER_ORIGIN}/api/documentation/upload`,
      ];

      let uploadedSuccessfully = false;

      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
            },
            body: formData,
          });

          if (res.ok) {
            await res.json();
            toast.success("Document uploaded successfully!");
            uploadedSuccessfully = true;
            setIsUploadOpen(false);
            setUploadForm({
              title: "",
              category: "User Manual",
              customCategory: "",
              pages: "",
              description: "",
              file: null,
            });
            fetchDocuments();
            break;
          }
        } catch (e) {
          // try next
        }
      }

      if (!uploadedSuccessfully) {
        // Fallback optimistic add
        const fileSizeMB =
          (uploadForm.file.size / (1024 * 1024)).toFixed(1) + " MB";
        const newLocalDoc = {
          _id: `local-${Date.now()}`,
          title: uploadForm.title.trim(),
          category: categoryName,
          date: new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
          pages: Number(uploadForm.pages) || 1,
          size: fileSizeMB,
          fileUrl: URL.createObjectURL(uploadForm.file),
          description: uploadForm.description,
        };

        setDocuments((prev) => [newLocalDoc, ...prev]);
        toast.success("Document saved successfully!");
        setIsUploadOpen(false);
        setUploadForm({
          title: "",
          category: "User Manual",
          customCategory: "",
          pages: "",
          description: "",
          file: null,
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload document");
    } finally {
      setIsUploading(false);
    }
  };

  // Delete Document
  const handleDeleteDoc = async (id) => {
    try {
      const base = getBaseApiUrl();
      const endpoints = [
        `${base}/api/documentation/${id}`,
        `/api/documentation/${id}`,
        `${SERVER_ORIGIN}/api/documentation/${id}`,
      ];

      for (const endpoint of endpoints) {
        try {
          await fetch(endpoint, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
            },
          });
        } catch (e) {}
      }

      setDocuments((prev) => prev.filter((d) => d._id !== id));
      toast.success("Document removed");
      setDeleteConfirmId(null);
    } catch (err) {
      toast.error("Error deleting document");
    }
  };

  // Helper to resolve PDF URL
  const resolvePdfUrl = (url) => {
    if (!url) return "/new-mou-sample-pdf.pdf";
    if (
      url.startsWith("blob:") ||
      url.startsWith("data:") ||
      url.startsWith("http")
    ) {
      return url;
    }
    const base = getBaseApiUrl();
    if (url.startsWith("/")) {
      return `${base}${url}`;
    }
    return `${base}/${url}`;
  };

  // Open Preview Modal
  const handleViewDocument = (doc) => {
    const fullUrl = resolvePdfUrl(doc.fileUrl);
    setPreviewDoc({ ...doc, fullUrl });
  };

  return (
    <div className={css.outerDiv}>
      <Toaster position="top-right" />

      {/* Normal Back Button Row */}
      <div className={css.headerSection}>
        <BackButton
          text="Back to Dashboard"
          customPath={getDashboardPath()}
        />
      </div>

      {/* Normal Page Header Row */}
      <div className={css.pageHeaderRow}>
        <div>
          <h1 className={css.pageTitle}>Documentation</h1>
          <p className={css.pageSubtitle}>
            You can view, upload, and download user manuals and project documentation from here
          </p>
        </div>

        {isAdmin && !isReadOnly && (
          <button
            className={css.uploadBtn}
            onClick={() => setIsUploadOpen(true)}
          >
            <CloudUploadIcon fontSize="small" />
            <span>Upload Document</span>
          </button>
        )}
      </div>

      {/* Filter, Search & View Toggle Toolbar */}
      <div className={css.toolbarContainer}>
        <div className={css.toolbarLeft}>
          {/* Category Filter Pills (displayed only when multiple categories exist) */}
          {documents.length > 0 && Object.keys(categoriesList).length > 2 && (
            <div className={css.categoryPills}>
              {Object.entries(categoriesList).map(([catName, count]) => (
                <button
                  key={catName}
                  type="button"
                  className={`${css.categoryPill} ${
                    selectedCategory === catName ? css.categoryPillActive : ""
                  }`}
                  onClick={() => setSelectedCategory(catName)}
                >
                  <span>{catName}</span>
                  <span className={css.pillBadge}>{count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Search Box */}
          <div className={css.searchWrapper}>
            <SearchIcon className={css.searchIcon} />
            <input
              type="text"
              className={css.searchInput}
              placeholder="Search by title, category, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* View Mode Toggle: Cards View vs List View */}
        <div className={css.toolbarRight}>
          <div className={css.viewModeToggle}>
            <button
              type="button"
              className={`${css.viewModeBtn} ${
                viewMode === "cards" ? css.viewModeBtnActive : ""
              }`}
              onClick={() => setViewMode("cards")}
              title="Cards View"
            >
              <GridViewIcon fontSize="small" />
              <span>Cards View</span>
            </button>
            <button
              type="button"
              className={`${css.viewModeBtn} ${
                viewMode === "list" ? css.viewModeBtnActive : ""
              }`}
              onClick={() => setViewMode("list")}
              title="List View"
            >
              <ViewListIcon fontSize="small" />
              <span>List View</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content: Cards View vs List View */}
      {loading ? (
        <Loader />
      ) : filteredDocuments.length === 0 ? (
        <div className={css.emptyState}>
          <h1 className="nothingText">Sorry, we found nothing</h1>
          {isAdmin && !isReadOnly && (
            <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
              <button
                className={`${css.addBtn} commonBtn`}
                onClick={() => setIsUploadOpen(true)}
              >
                <CloudUploadIcon fontSize="small" /> Upload First Document
              </button>
            </div>
          )}
        </div>
      ) : viewMode === "cards" ? (
        /* ================= 1. CARDS VIEW (GRID) ================= */
        <CardGrid>
          {filteredDocuments.map((doc) => {
            const pdfUrl = resolvePdfUrl(doc.fileUrl);

            return (
              <div
                className="col-lg-4 col-md-6 col-sm-12 cardOuterDiv"
                key={doc._id}
              >
                <div className={css.docCardDiv}>
                  {/* Top Visual Banner */}
                  <div className={css.cardBanner}>
                    <div className={css.pdfIconCircle}>
                      <PictureAsPdfIcon
                        style={{ fontSize: 32, color: "#ea4335" }}
                      />
                    </div>
                    <span className={css.cardCategoryTag}>
                      {doc.category || "User Manual"}
                    </span>
                  </div>

                  {/* Card Details */}
                  <div className={css.cardDetails}>
                    <p className={css.cardName} title={doc.title}>
                      {doc.title}
                    </p>

                    <p className={css.cardDesc}>
                      {doc.description ||
                        "Official YUVA platform documentation and reference guide."}
                    </p>

                    <p className={css.cardCount}>
                      {doc.pages || 1} Pages • {doc.size || "2.5 MB"} • {doc.date}
                    </p>

                    {/* Action Buttons */}
                    <div className={css.cardBtns}>
                      <button
                        className={css.cardViewBtn}
                        onClick={() => handleViewDocument(doc)}
                        title="View Document"
                      >
                        <VisibilityIcon fontSize="small" />
                        <span>View</span>
                      </button>

                      <a
                        href={pdfUrl}
                        download={doc.title || "document.pdf"}
                        className={css.cardDownloadBtn}
                        title="Download Document"
                      >
                        <DownloadIcon fontSize="small" />
                        <span>Download</span>
                      </a>

                      {isAdmin && !isReadOnly && (
                        <button
                          className={css.cardDltBtn}
                          onClick={() => setDeleteConfirmId(doc._id)}
                          title="Delete Document"
                        >
                          <DeleteOutlineIcon
                            style={{ color: "var(--card-dlt-red)" }}
                            fontSize="small"
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </CardGrid>
      ) : (
        /* ================= 2. LIST VIEW CARDS ================= */
        <div className={css.listContainer}>
          {filteredDocuments.map((doc) => {
            const pdfUrl = resolvePdfUrl(doc.fileUrl);

            return (
              <div className={css.listCardDiv} key={doc._id}>
                <div className={css.listLeft}>
                  <div className={css.listPdfIcon}>
                    <PictureAsPdfIcon
                      style={{ fontSize: 28, color: "#ea4335" }}
                    />
                  </div>

                  <div className={css.listInfo}>
                    <div className={css.listTitleRow}>
                      <h4 className={css.listTitle}>{doc.title}</h4>
                      <span className={css.listCategoryTag}>
                        {doc.category || "User Manual"}
                      </span>
                    </div>

                    <p className={css.listDesc}>
                      {doc.description ||
                        "Official YUVA platform documentation and reference guide."}
                    </p>

                    <div className={css.listMetaRow}>
                      <span className={css.listMetaItem}>
                        <strong>Pages:</strong> {doc.pages || 1}
                      </span>
                      <span>•</span>
                      <span className={css.listMetaItem}>
                        <strong>Size:</strong> {doc.size || "2.5 MB"}
                      </span>
                      <span>•</span>
                      <span className={css.listMetaItem}>
                        <strong>Date:</strong> {doc.date}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={css.listRight}>
                  <button
                    className={css.cardViewBtn}
                    style={{ flex: "initial", padding: "0 1.2rem" }}
                    onClick={() => handleViewDocument(doc)}
                    title="View Document"
                  >
                    <VisibilityIcon fontSize="small" />
                    <span>View</span>
                  </button>

                  <a
                    href={pdfUrl}
                    download={doc.title || "document.pdf"}
                    className={css.cardDownloadBtn}
                    title="Download Document"
                  >
                    <DownloadIcon fontSize="small" />
                    <span>Download</span>
                  </a>

                  {isAdmin && !isReadOnly && (
                    <button
                      className={css.cardDltBtn}
                      onClick={() => setDeleteConfirmId(doc._id)}
                      title="Delete Document"
                    >
                      <DeleteOutlineIcon
                        style={{ color: "var(--card-dlt-red)" }}
                        fontSize="small"
                      />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ================= Upload Document Modal ================= */}
      <Dialog
        open={isUploadOpen}
        onClose={() => !isUploading && setIsUploadOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
            color: "var(--yuva-dark-blue)",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <span>Upload Document</span>
          <button
            onClick={() => setIsUploadOpen(false)}
            disabled={isUploading}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#64748b",
            }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </DialogTitle>

        <form onSubmit={handleUploadSubmit}>
          <DialogContent style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Document Title */}
              <TextField
                label="Document Title"
                placeholder="e.g. Apda Mitra User Manual"
                value={uploadForm.title}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, title: e.target.value })
                }
                fullWidth
                required
                size="small"
              />

              {/* Category Select */}
              <TextField
                select
                label="Category"
                value={uploadForm.category}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, category: e.target.value })
                }
                fullWidth
                size="small"
              >
                <MenuItem value="User Manual">User Manual</MenuItem>
                <MenuItem value="SRS">SRS</MenuItem>
                <MenuItem value="Trainer Manual">Trainer Manual</MenuItem>
                <MenuItem value="Student Guide">Student Guide</MenuItem>
                <MenuItem value="Policy">Policy</MenuItem>
                <MenuItem value="Custom">+ Other (Custom)</MenuItem>
              </TextField>

              {uploadForm.category === "Custom" && (
                <TextField
                  label="Custom Category Name"
                  placeholder="e.g. Circular, SOP, Report"
                  value={uploadForm.customCategory}
                  onChange={(e) =>
                    setUploadForm({
                      ...uploadForm,
                      customCategory: e.target.value,
                    })
                  }
                  fullWidth
                  required
                  size="small"
                />
              )}

              {/* Estimated Pages */}
              <TextField
                label="Number of Pages (Optional)"
                type="number"
                placeholder="e.g. 25"
                value={uploadForm.pages}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, pages: e.target.value })
                }
                fullWidth
                size="small"
                inputProps={{ min: 1 }}
              />

              {/* Description */}
              <TextField
                label="Description (Optional)"
                placeholder="Brief summary of document content..."
                multiline
                rows={2}
                value={uploadForm.description}
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, description: e.target.value })
                }
                fullWidth
                size="small"
              />

              {/* File Upload Zone */}
              <div>
                <label
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--yuva-dark-blue)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Upload PDF File *
                </label>
                <div
                  style={{
                    border: "2px dashed #cbd5e1",
                    borderRadius: 8,
                    padding: "24px 16px",
                    textAlign: "center",
                    backgroundColor: "#f8fafc",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    document.getElementById("doc-file-input").click()
                  }
                >
                  <PictureAsPdfIcon
                    style={{ fontSize: 36, color: "#ea4335" }}
                  />
                  <p
                    style={{
                      margin: "8px 0 4px",
                      fontSize: 14,
                      fontWeight: 600,
                      color: "var(--yuva-dark-blue)",
                    }}
                  >
                    {uploadForm.file
                      ? uploadForm.file.name
                      : "Click to select a PDF document"}
                  </p>
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    {uploadForm.file
                      ? `${(
                          uploadForm.file.size /
                          (1024 * 1024)
                        ).toFixed(2)} MB`
                      : "Only PDF format supported (Max 50MB)"}
                  </span>
                  <input
                    id="doc-file-input"
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setUploadForm({
                          ...uploadForm,
                          file: e.target.files[0],
                        });
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </DialogContent>

          <DialogActions
            style={{
              padding: "12px 24px",
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <Button
              onClick={() => setIsUploadOpen(false)}
              disabled={isUploading}
              style={{ color: "#64748b", textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                isUploading || !uploadForm.title.trim() || !uploadForm.file
              }
              style={{
                backgroundColor: "var(--yuva-blue)",
                textTransform: "none",
                fontWeight: 600,
                padding: "6px 20px",
              }}
            >
              {isUploading ? (
                <>
                  <CircularProgress
                    size={18}
                    style={{ color: "white", marginRight: 8 }}
                  />
                  Uploading...
                </>
              ) : (
                "Upload Document"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ================= PDF Preview Modal ================= */}
      <Dialog
        open={Boolean(previewDoc)}
        onClose={() => setPreviewDoc(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 20px",
            backgroundColor: "var(--yuva-dark-blue)",
            color: "white",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <PictureAsPdfIcon style={{ color: "#ff8080", fontSize: 22 }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {previewDoc?.title}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <a
              href={previewDoc?.fullUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                color: "white",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                textDecoration: "none",
                fontSize: 13,
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: "5px 12px",
                borderRadius: 4,
              }}
            >
              <OpenInNewIcon fontSize="small" /> New Tab
            </a>
            <a
              href={previewDoc?.fullUrl}
              download={previewDoc?.title || "document.pdf"}
              style={{
                color: "white",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                textDecoration: "none",
                fontSize: 13,
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: "5px 12px",
                borderRadius: 4,
              }}
            >
              <DownloadIcon fontSize="small" /> Download
            </a>
            <button
              onClick={() => setPreviewDoc(null)}
              style={{
                background: "none",
                border: "none",
                color: "white",
                cursor: "pointer",
                padding: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </DialogTitle>

        <DialogContent
          style={{ padding: 0, height: "78vh", backgroundColor: "#334155" }}
        >
          {previewDoc && (
            <iframe
              src={`${previewDoc.fullUrl}#toolbar=1`}
              title={previewDoc.title}
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ================= Delete Confirmation Modal ================= */}
      <Dialog
        open={Boolean(deleteConfirmId)}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ fontWeight: 600, fontSize: 18 }}>
          Delete Document?
        </DialogTitle>
        <DialogContent>
          <p style={{ color: "#64748b", margin: 0 }}>
            Are you sure you want to delete this document? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px" }}>
          <Button
            onClick={() => setDeleteConfirmId(null)}
            style={{ color: "#64748b", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteDoc(deleteConfirmId)}
            variant="contained"
            color="error"
            style={{ textTransform: "none", fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DocumentationPage;
